import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw, CheckCircle2, AlertCircle, XCircle, Wrench, Loader2, Database,
  TrendingUp, TrendingDown, Activity,
} from 'lucide-react';

import { API_BASE_URL as API } from '../utils/apiConfig';

const fmtN = (n) => (n === null || n === undefined ? '—' : n.toLocaleString());
const fmtCurrency = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(2)}K`;
  return `₹${Number(n).toFixed(0)}`;
};
const fmtPct = (p) => (p === null || p === undefined ? '—' : `${p > 0 ? '+' : ''}${p.toFixed(2)}%`);

const StatusPill = ({ status }) => {
  const map = {
    in_sync: { cls: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'In Sync' },
    drift: { cls: 'bg-yellow-500/20 text-yellow-400', icon: AlertCircle, label: 'Drift' },
    upstream_error: { cls: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Upstream Error' },
    csv_only: { cls: 'bg-purple-500/20 text-purple-400', icon: Database, label: 'CSV' },
    local_only: { cls: 'bg-slate-500/20 text-slate-400', icon: Database, label: 'Local Only' },
  };
  const c = map[status] || map.local_only;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${c.cls}`}>
      <Icon className="w-3 h-3" />
      {c.label}
    </span>
  );
};

const Delta = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-muted-foreground">—</span>;
  const ok = Math.abs(pct) <= 1;
  const Icon = pct >= 0 ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? 'text-muted-foreground' : pct > 0 ? 'text-yellow-400' : 'text-red-400'}`}>
      {!ok && <Icon className="w-3 h-3" />}
      {fmtPct(pct)}
    </span>
  );
};

export default function DataReconciliation() {
  const { isSuperAdmin } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skipUpstream, setSkipUpstream] = useState(true);
  const [repairing, setRepairing] = useState({});

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/sync/reconcile?skip_upstream=${skipUpstream ? 'true' : 'false'}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        toast.error('Failed to load reconciliation');
        return;
      }
      const data = await res.json();
      setReport(data);
      const issues = (data.brands || []).filter((b) => b.flags && b.flags.length > 0).length;
      toast.success(`Reconciliation loaded — ${issues} brand${issues === 1 ? '' : 's'} with drift`);
    } catch (e) {
      console.error(e);
      toast.error('Reconciliation request failed');
    } finally {
      setLoading(false);
    }
  }, [skipUpstream]);

  const repairBrand = async (bid) => {
    setRepairing((r) => ({ ...r, [bid]: true }));
    try {
      const res = await fetch(`${API}/api/sync/reconcile/repair/${bid}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.result?.success) {
        toast.success(`Repaired aggregated_stats for ${bid}`);
      } else {
        toast.error(`Repair failed: ${data.result?.error || 'unknown'}`);
      }
      await fetchReport();
    } catch (e) {
      toast.error('Repair request failed');
    } finally {
      setRepairing((r) => ({ ...r, [bid]: false }));
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
        <Database className="w-12 h-12 mb-4 opacity-50" />
        <p>Only super admins can view reconciliation</p>
      </div>
    );
  }

  const summary = report?.summary || {};
  const totalLocal = summary.local_customers || 0;
  const totalTxn = summary.local_transactions || 0;
  const totalRev = summary.local_revenue || 0;
  const byStatus = summary.by_status || {};

  return (
    <div className="space-y-6" data-testid="data-reconciliation">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Data Reconciliation
          </h2>
          <p className="text-sm text-muted-foreground">
            Cross-figure tally: local collection vs aggregated_stats vs upstream Data Bridge
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={skipUpstream}
              onChange={(e) => setSkipUpstream(e.target.checked)}
              data-testid="skip-upstream-toggle"
            />
            Skip upstream (faster)
          </label>
          <Button
            onClick={fetchReport}
            disabled={loading}
            data-testid="run-reconcile-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Run Reconciliation
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">Total Customers (local)</p>
            <p className="text-2xl font-bold">{fmtN(totalLocal)}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">Total Transactions (local)</p>
            <p className="text-2xl font-bold">{fmtN(totalTxn)}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">Total Revenue (local sum)</p>
            <p className="text-2xl font-bold">{fmtCurrency(totalRev)}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">By Status</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(byStatus).map(([s, n]) => (
                <Badge key={s} variant="secondary" className="text-xs">
                  {s}: {n}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!report && !loading && (
        <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
          <Activity className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            Click <b>Run Reconciliation</b> to tally every brand&apos;s local, aggregated_stats and
            (optionally) upstream Data Bridge figures.
          </p>
        </div>
      )}

      {/* Report table */}
      {report && (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="reconcile-table">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-sm">Per-Brand Reconciliation</h3>
            <span className="text-xs text-muted-foreground">
              Generated: {new Date(report.generated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Brand</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium">Customers<br /><span className="text-[10px] text-muted-foreground">local / agg / upstream</span></th>
                  <th className="text-right p-3 font-medium">Transactions<br /><span className="text-[10px] text-muted-foreground">local / agg / upstream</span></th>
                  <th className="text-right p-3 font-medium">Revenue<br /><span className="text-[10px] text-muted-foreground">local sum / agg</span></th>
                  <th className="text-left p-3 font-medium">Last Txn<br /><span className="text-[10px] text-muted-foreground">local / upstream</span></th>
                  <th className="text-left p-3 font-medium">Flags</th>
                  <th className="text-right p-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {(report.brands || []).map((b) => {
                  const c = b.counts?.customers || {};
                  const t = b.counts?.transactions || {};
                  const r = b.revenue || {};
                  const lt = b.last_transaction || {};
                  return (
                    <tr key={b.brand_id} className="border-t border-border hover:bg-muted/30">
                      <td className="p-3">
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.brand_id} · {b.source === 'manual_csv' ? 'CSV' : 'API'}</div>
                      </td>
                      <td className="p-3"><StatusPill status={b.status} /></td>
                      <td className="p-3 text-right">
                        <div className="font-mono">{fmtN(c.local_collection)}</div>
                        <div className="text-xs text-muted-foreground font-mono">{fmtN(c.aggregated_stats)} / {fmtN(c.upstream)}</div>
                        <div className="text-xs flex justify-end gap-2">
                          <span title="local vs agg">A:<Delta pct={c.local_vs_aggstats_pct} /></span>
                          <span title="local vs upstream">U:<Delta pct={c.local_vs_upstream_pct} /></span>
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="font-mono">{fmtN(t.local_collection)}</div>
                        <div className="text-xs text-muted-foreground font-mono">{fmtN(t.aggregated_stats)} / {fmtN(t.upstream)}</div>
                        <div className="text-xs flex justify-end gap-2">
                          <span title="local vs agg">A:<Delta pct={t.local_vs_aggstats_pct} /></span>
                          <span title="local vs upstream">U:<Delta pct={t.local_vs_upstream_pct} /></span>
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="font-mono text-xs">{fmtCurrency(r.local_sum)}</div>
                        <div className="text-xs text-muted-foreground font-mono">{fmtCurrency(r.aggregated_stats)}</div>
                        <div className="text-xs"><Delta pct={r.pct_diff} /></div>
                      </td>
                      <td className="p-3 text-xs">
                        <div className="font-mono">{lt.local?.datetime || '—'}</div>
                        <div className="text-muted-foreground font-mono">{lt.upstream?.datetime || '—'}</div>
                        {lt.delta_seconds !== null && lt.delta_seconds !== undefined && (
                          <div className={`${Math.abs(lt.delta_seconds) > 60 ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                            Δ {Math.round(lt.delta_seconds / 60)}m
                          </div>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {(b.flags || []).map((f) => (
                            <span
                              key={f}
                              className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/30"
                            >
                              {f}
                            </span>
                          ))}
                          {b.upstream_error && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-500/10 text-red-400 border border-red-500/30" title={b.upstream_error}>
                              upstream_err
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        {(b.flags || []).some((f) => f.startsWith('agg_stats_')) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => repairBrand(b.brand_id)}
                            disabled={!!repairing[b.brand_id]}
                            data-testid={`repair-btn-${b.brand_id}`}
                            title="Recalculate aggregated_stats from local transactions"
                          >
                            {repairing[b.brand_id] ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <>
                                <Wrench className="w-3.5 h-3.5 mr-1" />
                                Repair
                              </>
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
